/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Chip,
  TableContainer,
  Paper,
  Box,
  Modal,
  Typography,
  CircularProgress,
  Tooltip,
  Checkbox,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import CopyToClipboardButton from 'renderer/shared/components/CopyToClipboardButton';
import { useGetProductByPriceIdQuery } from 'renderer/features/products-page/services/products.endpoints';
import HostAppsDropdown from 'renderer/features/purchase-page/components/HostAppsDropdown';
import { Form, Formik } from 'formik';
import {
  useUpdateLicenseUsableMutation,
  usePreviewSubscriptionChangeQuery,
  useUpgradeSubscriptionMutation,
  useDowngradeSubscriptionMutation,
  useCancelSubscriptionMutation,
  useReleaseScheduleMutation,
} from '../services/users.endpoints';
import {
  Subscription as SubscriptionType,
  Subscriptions as SubscriptionsType,
} from '../schemas/users.schemas';
import InvoiceDetails from './InvoiceDetails';
import SubscriptionKeyDetails from './SubscriptionKeyDetails';

const getChipColor = (subscription: SubscriptionType) => {
  if (subscription.status === 'canceled') return 'error.main';
  if (subscription.status === 'pending') return 'warning.light';
  if (subscription.scheduledChange === 'cancel') return 'error.main';
  if (subscription.scheduledChange === 'downgrade') return 'warning.light';
  return 'success.main';
  // if (subscription.status === 'trialing') return 'success.main';
};

const formatBillingDate = (subscription: SubscriptionType) =>
  moment(subscription.billingPeriodEndDate).format('MM/DD/YY');

const labelStatusChip = (subscription: SubscriptionType) => {
  if (subscription.status === 'canceled') {
    return `Canceled\n${formatBillingDate(subscription)}`;
  }
  if (subscription.status !== 'canceled' && !!subscription.scheduledChange) {
    return `${_.capitalize(subscription.scheduledChange)}s\n${formatBillingDate(
      subscription,
    )}`;
  }
  return `Renews \n${formatBillingDate(subscription)}`;
};

const tableStyles = {
  tableContainer: {
    // marginTop: '20px',
    marginBottom: '20px',
    border: '1px solid rgba(224, 224, 224, 1)',
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableCell: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  tableRow: {
    '&:last-child td': {
      border: 0,
    },
  },
};

interface SubscriptionsProps {
  subscriptions: SubscriptionsType;
  userId: string;
  refetchUserDetails: () => void;
}

export default function Subscriptions({
  subscriptions,
  userId,
  refetchUserDetails,
}: SubscriptionsProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [serialModalOpen, setSerialModalOpen] = useState(false);
  const [serialDetails, setSerialDetails] = useState<
    SubscriptionType | undefined
  >(undefined);
  const [previewDetails, setPreviewDetails] = useState<any>('');
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [subscriptionIdToRemove, setSubscriptionIdToRemove] =
    useState<string>(''); // New state for tracking the subscription ID for removal

  const [upgradeSubscription] = useUpgradeSubscriptionMutation();
  const [downgradeSubscription] = useDowngradeSubscriptionMutation();
  const [cancelSubscription] = useCancelSubscriptionMutation();
  const [updateLicenseUsable] = useUpdateLicenseUsableMutation();
  const { data: newProduct } = useGetProductByPriceIdQuery(
    currentSubscription ? currentSubscription.newPlanId : '',
  );

  const initialForm = {
    hostApplication:
      newProduct &&
      newProduct.supportedHostApplications &&
      newProduct.supportedHostApplications.length === 1
        ? newProduct.supportedHostApplications[0]
        : {
            id: '',
            version: '',
            displayName: '',
          },
  };
  // const { refetch } = useGetUserDetailsQuery(userId, { skip: true });

  const handleCheckboxChange = async (event: any, serial: string) => {
    const { checked } = event.target;
    try {
      await updateLicenseUsable({
        serial,
        status: checked,
        userId,
      }).unwrap();
      refetchUserDetails(); // Force refetch after mutation
    } catch (error) {
      console.error('Failed to update license group:', error);
    }
  };

  const isWide = useMediaQuery('(min-width:1400px)');

  const [fetchData, setFetchData] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [newPlanId, setNewPlanId] = useState<string | null>(null);

  const [confirmLoading, setConfirmLoading] = useState(false);

  const [hoveredSubscriptionId, setHoveredSubscriptionId] = useState<
    string | null
  >(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [scheduledChange, setScheduledChange] = useState<string | undefined>(
    '',
  );

  const [releaseSchedule] = useReleaseScheduleMutation();
  const handleRemoveScheduledUpdate = async (subId: string) => {
    try {
      const result = await releaseSchedule(subId);
      console.log('Scheduled change removed successfully:', result);
      setShowConfirmModal(false); // Close the modal upon successful removal
    } catch (error) {
      console.error('Error removing scheduled change:', error);
    }
  };

  const openConfirmModal = (
    _subscriptionId: string,
    changeType: string | undefined,
  ) => {
    setSubscriptionIdToRemove(_subscriptionId); // Set the subscription ID for removal
    setScheduledChange(changeType); // Set the type of change
    setShowConfirmModal(true); // Show the confirmation modal
  };

  // Assume usePreviewSubscriptionChangeQuery is a hook for fetching data
  const { data, error, isLoading } = usePreviewSubscriptionChangeQuery({
    subscriptionId,
    action,
    newPlanId,
    enabled: fetchData, // Query will only run when fetchData is true
  });

  useEffect(() => {
    if (fetchData && !isLoading) {
      if (data) {
        // Assuming data needs to be formatted or processed before displaying
        // setPreviewDetails(JSON.stringify(data)); // or a more appropriate formatting
        setPreviewDetails(<InvoiceDetails data={data} />);
        setModalOpen(true); // Ensure modal is open after data is processed
      }
      if (error) {
        setPreviewDetails(`Failed to fetch details: ${(error as any).message}`);
        setModalOpen(true); // Still open the modal to show the error
      }
      setFetchData(false); // Reset after handling
    }
  }, [data, error, isLoading, fetchData]);

  const handlePreview = async (
    _subscriptionId: string,
    _action: string,
    _newPlanId: string | null,
  ) => {
    console.log('handlePreview');

    // Set the subscription details that will be needed for confirmation
    setCurrentSubscription({
      subscriptionId: _subscriptionId,
      action: _action,
      newPlanId: _newPlanId,
    });
    // Set parameters
    setSubscriptionId(_subscriptionId);
    setAction(_action);
    setNewPlanId(_newPlanId);

    // Determine if we should fetch data or simply inform the user
    if (_action === 'downgrade' || _action === 'cancel') {
      // Directly set a message about when the change will take effect
      setPreviewDetails(
        'Changes will take effect at the end of your current billing period.',
      );
    } else {
      // Continue with fetching preview data for upgrades
      setFetchData(true);
    }

    // // Open the modal here so it's ready to show the loading or fetched data
    setModalOpen(true);
    // setFetchData(true); // This will trigger the fetching of data

    // Since we want to show a preview, assume the fetched 'data' will be used for this
    // Wait for the data fetching to be triggered and completed
    if (!isLoading && data && _action === 'upgrade') {
      console.log('setPreviewDetails');
      setPreviewDetails(JSON.stringify(data)); // Or format the data as needed for display
    } else if (error && _action === 'upgrade') {
      setPreviewDetails(`Failed to fetch details: ${(error as any).message}`);
    }
  };

  // Handle confirm action
  const handleConfirm = async (values: typeof initialForm) => {
    console.log('handleConfirm');
    if (!currentSubscription) return;
    console.log('currentSubscription.action', currentSubscription.action);
    setConfirmLoading(true); // Start loading
    setPreviewDetails(<CircularProgress size={70} thickness={5} />); // Show loading spinner as previewDetails
    switch (currentSubscription.action) {
      case 'upgrade':
        await upgradeSubscription({
          subscriptionId: currentSubscription.subscriptionId,
          newPlanId: currentSubscription.newPlanId,
          hostApplication:
            newProduct &&
            newProduct.supportedHostApplications &&
            newProduct.supportedHostApplications.length === 1
              ? newProduct.supportedHostApplications[0]
              : values.hostApplication,
        });
        break;
      case 'downgrade':
        await downgradeSubscription({
          subscriptionId: currentSubscription.subscriptionId,
          newPlanId: currentSubscription.newPlanId,
          hostApplication: values.hostApplication,
        });
        break;
      case 'cancel':
        await cancelSubscription(currentSubscription.subscriptionId);
        break;
      default:
        break;
    }
    console.log('Closing modal');
    setConfirmLoading(false);
    setModalOpen(false);
  };

  return (
    <>
      <SubscriptionKeyDetails
        open={serialModalOpen}
        handleClose={() => setSerialModalOpen(false)}
        subscription={serialDetails}
      />
      <TableContainer component={Paper} sx={tableStyles.tableContainer}>
        <Table sx={tableStyles.table} aria-label="licenses table" size="small">
          <TableHead sx={tableStyles.tableHead}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>PRODUCT</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                SUBSCRIPTION KEY
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                STATUS
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                USEABLE BY THIS ACCOUNT
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.map((subscription) => (
              <TableRow
                key={subscription._id}
                sx={{
                  '&:last-child td': {
                    border: 0,
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)', // Light grey background on hover
                    // cursor: 'pointer' // Optional: changes the cursor to a pointer on hover
                  },
                }}
                onMouseEnter={() => {
                  setHoveredSubscriptionId(subscription._id);
                }}
                onMouseLeave={() => {
                  setHoveredSubscriptionId(null);
                }}
              >
                <TableCell>
                  {`${subscription.name}${
                    subscription.hostApp ? `(${subscription.hostApp})` : ''
                  }`}
                  {/* {subscription.name} ({subscription.hostApp}) */}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Button
                      onClick={() => {
                        setSerialModalOpen(true);
                        setSerialDetails(subscription);
                      }}
                    >
                      {subscription.serial}{' '}
                    </Button>
                    <CopyToClipboardButton textToCopy={subscription.serial} />
                  </Box>
                </TableCell>
                <TableCell
                  // align="center"
                  align={subscription.scheduledChange ? 'left' : 'center'}
                  // sx={{ display: 'flex', flexDirection: 'row' }}
                >
                  {subscription.scheduledChange && subscription.isOwner ? (
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                      <Chip
                        label={labelStatusChip(subscription)}
                        // color={
                        //   subscription.scheduledChange === 'downgrade'
                        //     ? 'default'
                        //     : 'error'
                        // }
                        color="error"
                        // icon={<AccessTimeIcon />}
                        onDelete={() => {
                          openConfirmModal(
                            subscription._id,
                            subscription.scheduledChange,
                          );
                        }}
                        deleteIcon={
                          <Tooltip
                            title={`Remove scheduled ${subscription.scheduledChange}`}
                          >
                            <CloseIcon />
                          </Tooltip>
                        }
                        sx={{
                          ml: 1,
                          borderRadius: 1,
                          height: 'auto',
                          padding: 0.5,
                          backgroundColor: getChipColor(subscription),
                          width: 150,
                          textAlign: 'center',
                          '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                            // whiteSpace: 'pre-line',
                          },
                          fontWeight: 'bold',
                          // '&:hover': {
                          //   cursor: 'pointer',
                          // },
                        }}
                      />
                    </Box>
                  ) : (
                    <Chip
                      sx={{
                        backgroundColor: getChipColor(subscription),
                        // whiteSpace: 'pre-wrap',
                        color: 'common.white',
                        ml: 1,
                        padding: 0.5,
                        borderRadius: 1,
                        fontWeight: 'bold',
                        height: 'auto',
                        width: 150,
                        textAlign: 'center',
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'pre-line',
                          // whiteSpace: 'pre-wrap',
                        },
                      }}
                      label={labelStatusChip(subscription)}
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={subscription.isUsable}
                    disabled={
                      !subscription.isOwner ||
                      subscription.status === 'canceled'
                    }
                    onChange={(e) =>
                      handleCheckboxChange(e, subscription.serial)
                    }
                  />
                </TableCell>
                <Tooltip
                  title={
                    subscription.isOwner
                      ? 'Manage Subscription'
                      : 'Contact License Owner'
                  }
                >
                  <TableCell align="center">
                    <Box
                      sx={
                        isWide ? { display: 'flex', flexDirection: 'row' } : {}
                      }
                    >
                      {subscription.upgradeId &&
                        !subscription.scheduledChange && (
                          <Button
                            size="small"
                            sx={{
                              mr: 1,
                            }}
                            variant="contained"
                            onClick={() =>
                              handlePreview(
                                subscription._id,
                                'upgrade',
                                subscription.upgradeId ?? null,
                              )
                            }
                            disabled={
                              confirmLoading ||
                              !subscription.isOwner ||
                              subscription.status === 'canceled'
                            }
                          >
                            Upgrade
                          </Button>
                        )}
                      {subscription.downgradeId &&
                        !subscription.scheduledChange && (
                          <Button
                            size="small"
                            sx={{
                              mr: 1,
                            }}
                            variant="outlined"
                            onClick={() =>
                              handlePreview(
                                subscription._id,
                                'downgrade',
                                subscription.downgradeId ?? null,
                              )
                            }
                            disabled={
                              !subscription.downgradeId ||
                              confirmLoading ||
                              !!subscription.scheduledChange ||
                              !subscription.isOwner ||
                              subscription.status === 'canceled'
                            }
                          >
                            Downgrade
                          </Button>
                        )}
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{
                          bgcolor: 'lightred',
                          '&:hover': { bgcolor: 'red', color: 'white' },
                          mr: 1,
                        }}
                        onClick={() =>
                          handlePreview(subscription._id, 'cancel', null)
                        }
                        disabled={
                          confirmLoading ||
                          subscription.scheduledChange === 'cancel' ||
                          !subscription.isOwner ||
                          subscription.status === 'canceled'
                        }
                      >
                        Cancel
                      </Button>
                    </Box>
                  </TableCell>
                </Tooltip>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="delete-modal-title" variant="h6" component="h2">
            Confirm
          </Typography>
          <Typography id="delete-modal-description" sx={{ mt: 2 }}>
            Are you sure you want to remove update to {scheduledChange} the
            plan?
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setShowConfirmModal(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                handleRemoveScheduledUpdate(subscriptionIdToRemove)
              }
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Formik
          initialValues={initialForm}
          onSubmit={(values) => handleConfirm(values)}
          validate={(values) => {
            console.log({ values });
          }}
        >
          <Form>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center', // Center children horizontally
                justifyContent: 'center', // Center children vertically
                // width: '80%', // Optional: Adjust width to fit content
                minHeight: '200px', // Optional: Prevent modal from collapsing when empty
              }}
            >
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Subscription Change {!confirmLoading ? 'Preview' : ''}
              </Typography>
              {newProduct && currentSubscription?.action === 'downgrade' && (
                <HostAppsDropdown
                  supportedHostApplications={
                    newProduct.supportedHostApplications
                  }
                />
              )}
              {isLoading || confirmLoading ? (
                <CircularProgress size={70} thickness={5} />
              ) : (
                <>
                  <Typography sx={{ flexGrow: 1 }}>{previewDetails}</Typography>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                  >
                    <Button
                      sx={{ mr: 2 }}
                      variant="contained"
                      // onClick={handleConfirm}
                      type="submit"
                      disabled={confirmLoading}
                    >
                      Confirm
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => setModalOpen(false)}
                    >
                      Close
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Form>
        </Formik>
      </Modal>
    </>
  );
}
